<template>
  <div class="onevr-homepage-main" @click="handleOutsideClick">
    <div class="onevr-animated-background">
      <div class="onevr-orb onevr-orb-1"></div>
      <div class="onevr-orb onevr-orb-2"></div>
      <div class="onevr-orb onevr-orb-3"></div>
      <div class="onevr-gradient-mesh"></div>
    </div>

    <div class="onevr-main-content">
      <section class="onevr-hero-wrapper">
        <div class="onevr-hero-inner">
          <div class="onevr-hero-badge-wrapper">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" class="onevr-badge-icon-svg">
              <path d="M8 0L10.5 5.5L16 8L10.5 10.5L8 16L5.5 10.5L0 8L5.5 5.5L8 0Z" fill="currentColor"/>
            </svg>
            <span class="onevr-badge-text-content">{{ $t("homepage.hero.badge") }}</span>
          </div>
          <h1 class="onevr-hero-main-title">
            <span class="onevr-hero-title-line">{{ $t("homepage.hero.title.line1") }}</span>
            <span class="onevr-hero-title-line onevr-hero-gradient-text">{{ $t("homepage.hero.title.line2") }}</span>
          </h1>
          <p class="onevr-hero-desc-text">{{ $t("homepage.hero.description") }}</p>
        </div>
      </section>

      <section class="onevr-features-wrapper">
        <div class="onevr-features-grid-container">
          <article
            v-for="(card, index) in cards"
            :key="card.label"
            class="onevr-feature-card-item"
            @click.stop="card.action"
            :style="{ animationDelay: `${index * 0.05}s` }"
            tabindex="0"
            @keypress.enter="card.action"
            role="button"
          >
            <div class="onevr-card-img-container">
              <img :src="card.imageUrl" :alt="card.fallbackLabel" class="onevr-card-main-image" loading="lazy"/>
              <div class="onevr-card-img-overlay"></div>
            </div>

            <div class="onevr-card-body-content">
              <div class="onevr-card-icon-wrapper">
                <img :src="card.iconUrl" class="onevr-card-feature-icon" :alt="`${card.fallbackLabel} Icon`"/>
              </div>

              <div class="onevr-card-text-area">
                <h3 class="onevr-card-title-text">{{ $t(card.label) || card.fallbackLabel }}</h3>
                <p class="onevr-card-desc-text">{{ card.description }}</p>
              </div>

              <div class="onevr-card-footer-area">
                <span class="onevr-card-cta-text">{{ $t("homepage.cards.exploreMore") }}</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" class="onevr-card-arrow-icon">
                  <path d="M7 3l7 7-7 7" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section class="onevr-integration-wrapper">
        <div class="onevr-integration-container">
          <div class="onevr-integration-badge">
            <span class="onevr-integration-dot"></span>
            <span class="onevr-integration-badge-label">{{ $t("homepage.integration.badge") }}</span>
          </div>
          <h2 class="onevr-integration-main-title">
            <span class="onevr-integration-title-accent">{{ $t("homepage.integration.title.accent") }}</span>
            {{ $t("homepage.integration.title.text") }}
          </h2>
          <p class="onevr-integration-subtitle-text">{{ $t("homepage.integration.subtitle") }}</p>

          <div class="onevr-integration-grid-container">
            <article
              class="onevr-integration-card-item"
              v-for="(item, idx) in integrationItems"
              :key="idx"
              :style="{ animationDelay: `${idx * 0.08}s` }"
            >
              <div class="onevr-integration-icon-box">
                <div class="onevr-integration-icon-bg"></div>
                <img :src="item.icon" class="onevr-integration-main-icon" :alt="`${item.title} Icon`" loading="lazy"/>
              </div>
              <h3 class="onevr-integration-card-title">{{ item.title }}</h3>
              <p class="onevr-integration-card-desc">{{ item.description }}</p>
            </article>
          </div>
        </div>
      </section>
    </div>

    <footer class="onevr-footer-main">
      <div class="onevr-footer-content-wrapper">
        <div class="onevr-footer-grid-layout">
          <div class="onevr-footer-col onevr-footer-company-col">
            <div class="onevr-footer-logo-wrapper">
              <img class="onevr-footer-logo-img" src="/assets/oneVRDarkBackLogo.png" alt="OneVR Logo"/>
            </div>
            <p class="onevr-footer-tagline-text">{{ $t("homepage.footer.tagline") }}</p>
            <p class="onevr-footer-company-text">pinestack GmbH</p>
          </div>

          <div class="onevr-footer-col">
            <h3 class="onevr-footer-heading-text">{{ $t("homepage.footer.company") }}</h3>
            <div class="onevr-footer-details-list">
              <div class="onevr-footer-address-item">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" class="onevr-footer-icon-svg">
                  <path d="M8 1a5 5 0 00-5 5c0 4 5 9 5 9s5-5 5-9a5 5 0 00-5-5z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <circle cx="8" cy="6" r="2" stroke="currentColor" stroke-width="1.5"/>
                </svg>
                <div>
                  <p>Postfach 1104</p>
                  <p>85626 Grasbrunn</p>
                </div>
              </div>
              <div class="onevr-footer-address-item">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" class="onevr-footer-icon-svg">
                  <path d="M8 1a5 5 0 00-5 5c0 4 5 9 5 9s5-5 5-9a5 5 0 00-5-5z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <circle cx="8" cy="6" r="2" stroke="currentColor" stroke-width="1.5"/>
                </svg>
                <div>
                  <p>Bülow Strasse 9</p>
                  <p>81679 München</p>
                </div>
              </div>
              <div class="onevr-footer-address-item">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" class="onevr-footer-icon-svg">
                  <path d="M8 1a5 5 0 00-5 5c0 4 5 9 5 9s5-5 5-9a5 5 0 00-5-5z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <circle cx="8" cy="6" r="2" stroke="currentColor" stroke-width="1.5"/>
                </svg>
                <div>
                  <p>An der Spinnerei 6</p>
                  <p>D-96047 Bamberg</p>
                </div>
              </div>
            </div>
          </div>

          <div class="onevr-footer-col">
            <h3 class="onevr-footer-heading-text">{{ $t("homepage.footer.getInTouch") }}</h3>
            <div class="onevr-footer-details-list">
              <a href="mailto:office@pinestack.io" class="onevr-footer-link-item">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" class="onevr-footer-icon-svg">
                  <path d="M2 5l5.5 3.5a2 2 0 002 0L15 5M3 13h10a2 2 0 002-2V5a2 2 0 00-2-2H3a2 2 0 00-2 2v6a2 2 0 002 2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                office@pinestack.io
              </a>
              <a href="https://www.pinestack.io" target="_blank" rel="noopener noreferrer" class="onevr-footer-link-item">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" class="onevr-footer-icon-svg">
                  <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.5"/>
                  <path d="M2 8h12M8 2a10 10 0 010 12M8 2a10 10 0 000 12" stroke="currentColor" stroke-width="1.5"/>
                </svg>
                www.pinestack.io
              </a>
              <button @click="goToContact" class="onevr-footer-contact-btn">
                <span>{{ $t("homepage.footer.connectButton") }}</span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8h10m0 0l-3-3m3 3l-3 3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div class="onevr-footer-bottom-area">
          <p class="onevr-footer-copyright-text">
            © {{ new Date().getFullYear() }} pinestack GmbH. {{ $t("homepage.footer.copyright") }}
          </p>
          <div class="onevr-footer-legal-links">
            <a href="#" class="onevr-footer-legal-link">{{ $t("homepage.footer.privacyPolicy") }}</a>
            <span class="onevr-footer-separator-dot">•</span>
            <a href="#" class="onevr-footer-legal-link">{{ $t("homepage.footer.termsOfService") }}</a>
            <span class="onevr-footer-separator-dot">•</span>
            <a href="#" class="onevr-footer-legal-link">{{ $t("homepage.footer.imprint") }}</a>
          </div>
        </div>
      </div>
    </footer>

    <ConnectivityDiagram ref="connectivityRef" style="margin-top: 15px"/>
    <BookingBrief ref="bookingBriefRef" style="margin-top: 15px"/>
    <AnalyticsBrief ref="analyticsRef" style="margin-top: 15px"/>
    <ClientSegments ref="clientSegmentsRef"/>
    <HardwareControlBrief ref="hardwareControlBriefRef" style="margin-top: 15px"/>
    <Operations ref="operationsBriefRef" style="margin-top: 15px"/>
    <Security ref="securityRef" style="margin-top: 15px"/>
    <Finance ref="financeRef" style="margin-top: 15px"/>
    <GeminiChatbot ref="geminiRef" style="margin-top: 15px"/>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import ConnectivityDiagram from "@/components/ConnectivityDiagram.vue";
import BookingBrief from "@/components/BookingBrief.vue";
import AnalyticsBrief from "@/components/AnalyticsBrief.vue";
import ClientSegments from "@/components/ClientSegments.vue";
import HardwareControlBrief from "@/components/HardwareControlBrief.vue";
import Operations from "@/components/Operations.vue";
import Security from "@/components/Security.vue";
import Finance from "@/components/Finance.vue";
import GeminiChatbot from "@/components/GeminiChatbot.vue";

const { t } = useI18n();
const router = useRouter();

const connectivityRef = ref(null);
const bookingBriefRef = ref(null);
const analyticsRef = ref(null);
const clientSegmentsRef = ref(null);
const hardwareControlBriefRef = ref(null);
const operationsBriefRef = ref(null);
const securityRef = ref(null);
const financeRef = ref(null);
const geminiRef = ref(null);

const handleOutsideClick = () => {};

const goToContact = () => {
  router.push({ name: "ContactUs" });
};

const integrationItems = computed(() => [
  {
    title: t("homepage.integrationItems.iot.title"),
    description: t("homepage.integrationItems.iot.description"),
    icon: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/home-automation.svg"
  },
  {
    title: t("homepage.integrationItems.thirdParty.title"),
    description: t("homepage.integrationItems.thirdParty.description"),
    icon: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/cloud.svg"
  },
  {
    title: t("homepage.integrationItems.analytics.title"),
    description: t("homepage.integrationItems.analytics.description"),
    icon: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/chart-areaspline.svg"
  },
  {
    title: t("homepage.integrationItems.automation.title"),
    description: t("homepage.integrationItems.automation.description"),
    icon: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/cog-outline.svg"
  }
]);

const cards = computed(() => {
  const cardsList = [
    {
      label: "app.visualization",
      fallbackLabel: "Visualization",
      description: t("homepage.cards.visualization.description") || "Explore interactive 2D/3D floor plans",
      iconUrl: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/vector-rectangle.svg",
      imageUrl: "/assets/home_images/Visualization.png",
      action: () => router.push({ name: "Visualization" })
    },
    {
      label: "app.infoCenter",
      fallbackLabel: "Info Center",
      description: t("homepage.cards.infoCenter.description") || "Stay informed with company news",
      iconUrl: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/newspaper-variant-outline.svg",
      imageUrl: "/assets/home_images/Info_Center.png",
      action: () => router.push({ name: "InfoCenter" })
    },
    {
      label: "app.incidents",
      fallbackLabel: "Incident Management",
      description: t("homepage.cards.incidents.description") || "Report and track facility incidents",
      iconUrl: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/alert-octagon-outline.svg",
      imageUrl: "/assets/home_images/Incident_Management.png",
      action: () => router.push({ name: "Incidents" })
    },
    {
      label: "app.platformConnectivity",
      fallbackLabel: "Platform Connectivity",
      description: t("homepage.cards.platformConnectivity.description") || "Seamless integration with third-party systems",
      iconUrl: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/lan-connect.svg",
      imageUrl: "/assets/home_images/Platform_Connectivity.png",
      action: () => connectivityRef.value?.open?.()
    },
    {
      label: "app.booking",
      fallbackLabel: "Room Booking",
      description: t("homepage.cards.booking.description") || "Book meeting rooms and workspaces",
      iconUrl: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/calendar-clock.svg",
      imageUrl: "/assets/home_images/Booking_Overview.png",
      action: () => bookingBriefRef.value?.open?.()
    },
    {
      label: "app.analytics",
      fallbackLabel: "Analytics & Insights",
      description: t("homepage.cards.analytics.description") || "Data-driven insights for better decisions",
      iconUrl: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/chart-box-outline.svg",
      imageUrl: "/assets/home_images/Analytics_Dashboard.png",
      action: () => analyticsRef.value?.open?.()
    },
    {
      label: "app.esg",
      fallbackLabel: "ESG Dashboard",
      description: t("homepage.cards.esg.description") || "Track environmental sustainability metrics",
      iconUrl: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/leaf.svg",
      imageUrl: "/assets/home_images/ESG_Dashboard.png",
      action: () => router.push({ name: "ESG" })
    },
    {
      label: "app.clientSegments",
      fallbackLabel: "Client Management",
      description: t("homepage.cards.clientSegments.description") || "Manage client relationships effectively",
      iconUrl: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/account-group.svg",
      imageUrl: "/assets/home_images/Client_Segments_Dashboard.png",
      action: () => router.push("/client-segment-analytics")
    },
    {
      label: "app.hardware",
      fallbackLabel: "Hardware Control",
      description: t("homepage.cards.hardware.description") || "Control building hardware systems",
      iconUrl: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/router-wireless-settings.svg",
      imageUrl: "/assets/home_images/Room_Control.png",
      action: () => hardwareControlBriefRef.value?.open?.()
    },
    {
      label: "app.operations",
      fallbackLabel: "Operations Management",
      description: t("homepage.cards.operations.description") || "Streamline facility operations",
      iconUrl: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/cog-outline.svg",
      imageUrl: "/assets/home_images/Ticket_Management.png",
      action: () => operationsBriefRef.value?.open?.()
    },
    {
      label: "app.security",
      fallbackLabel: "Security & Access",
      description: t("homepage.cards.security.description") || "Manage building security and access",
      iconUrl: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/shield-check.svg",
      imageUrl: "/assets/home_images/Security_Overview.png",
      action: () => securityRef.value?.open?.()
    },
    {
      label: "app.assistant",
      fallbackLabel: "AI Assistant",
      description: t("homepage.cards.assistant.description") || "Get help from AI-powered assistant",
      iconUrl: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/robot-outline.svg",
      imageUrl: "/assets/home_images/Intelligent_Assistant.png",
      action: () => geminiRef.value?.open?.()
    }
  ];
  return cardsList;
});

onMounted(() => {
  const observerOptions = {
    threshold: 0.1,
    rootMargin: "0px 0px -50px 0px"
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("onevr-is-visible");
      }
    });
  }, observerOptions);

  document.querySelectorAll(".onevr-feature-card-item, .onevr-integration-card-item").forEach((el) => observer.observe(el));
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700;900&display=swap');

.onevr-homepage-main * {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.onevr-homepage-main {
  position: relative;
  min-height: 100vh;
  min-height: 100dvh;
  background: linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 50%, #E2E8F0 100%);
  overflow-x: hidden;
  font-family: 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  width: 100%;
}

.onevr-animated-background {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
}

.onevr-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.2;
  animation: onevr-float-animation 25s ease-in-out infinite;
}

.onevr-orb-1 {
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(40, 80, 150, 0.2), transparent 70%);
  top: -15%;
  left: -10%;
}

.onevr-orb-2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(40, 80, 150, 0.15), transparent 70%);
  top: 40%;
  right: -8%;
  animation-delay: 3s;
}

.onevr-orb-3 {
  width: 450px;
  height: 450px;
  background: radial-gradient(circle, rgba(40, 80, 150, 0.18), transparent 70%);
  bottom: 20%;
  left: 20%;
  animation-delay: 6s;
}

@keyframes onevr-float-animation {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(30px, -30px) scale(1.05); }
}

.onevr-gradient-mesh {
  position: absolute;
  inset: 0;
  background: radial-gradient(at 20% 30%, rgba(40, 80, 150, 0.1), transparent 50%),
              radial-gradient(at 80% 70%, rgba(40, 80, 150, 0.08), transparent 50%);
  animation: onevr-mesh-rotate 40s linear infinite;
}

@keyframes onevr-mesh-rotate {
  from { transform: rotate(0deg) scale(1); }
  to { transform: rotate(360deg) scale(1.1); }
}

.onevr-main-content {
  position: relative;
  z-index: 1;
}

.onevr-hero-wrapper {
  padding: 40px 3rem 80px;
  text-align: center;
  max-width: 1300px;
  margin: 0 auto;
}

.onevr-hero-badge-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 28px;
  background: rgba(255, 255, 255, 0.95);
  border: 1.5px solid rgba(40, 80, 150, 0.25);
  border-radius: 50px;
  margin-bottom: 36px;
  box-shadow: 0 6px 20px rgba(40, 80, 150, 0.1);
  backdrop-filter: blur(12px);
  transition: all 0.3s ease;
}

.onevr-hero-badge-wrapper:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(40, 80, 150, 0.15);
}

.onevr-badge-icon-svg {
  color: #285096;
  animation: onevr-sparkle 3s ease-in-out infinite;
}

@keyframes onevr-sparkle {
  0%, 100% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(1.15); }
}

.onevr-badge-text-content {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 1.8px;
  text-transform: uppercase;
  color: #285096;
}

.onevr-hero-main-title {
  font-size: clamp(48px, 7vw, 82px);
  font-weight: 900;
  line-height: 1.1;
  margin: 0 0 32px;
  letter-spacing: -2.5px;
  color: #14151F;
}

.onevr-hero-title-line {
  display: block;
}

.onevr-hero-gradient-text {
  background: linear-gradient(135deg, #285096 0%, #1a3d6d 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.onevr-hero-desc-text {
  font-size: clamp(17px, 2vw, 21px);
  line-height: 1.8;
  color: rgba(20, 21, 31, 0.7);
  max-width: 950px;
  margin: 0 auto;
}

.onevr-features-wrapper {
  padding: 40px 3rem 60px;
  max-width: 1700px;
  margin: 0 auto;
}

.onevr-features-grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 32px;
  width: 100%;
}

.onevr-feature-card-item {
  position: relative;
  background: #FFFFFF;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  border: 1px solid #D4D9DE;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  opacity: 0;
  transform: translateY(30px);
}

.onevr-feature-card-item.onevr-is-visible {
  animation: onevr-fade-in-up 0.6s ease-out forwards;
}

@keyframes onevr-fade-in-up {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.onevr-feature-card-item:hover {
  transform: translateY(-8px);
  border-color: #285096;
  box-shadow: 0 10px 40px -10px rgba(40, 80, 150, 0.3);
}

.onevr-card-img-container {
  position: relative;
  width: 100%;
  height: 230px;
  overflow: hidden;
  flex-shrink: 0;
  background: #F6F8FA;
  border-bottom: 1px solid #F0F0F0;
}

.onevr-card-main-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 0.6s ease;
  display: block;
}

.onevr-feature-card-item:hover .onevr-card-main-image {
  transform: scale(1.05);
}

.onevr-card-img-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.05) 100%);
  pointer-events: none;
}

.onevr-card-body-content {
  padding: 0 24px 24px;
  display: flex;
  flex-direction: column;
  position: relative;
  flex: 1;
}

.onevr-card-icon-wrapper {
  position: absolute;
  top: -24px;
  left: 24px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #EDF3FD;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(40, 80, 150, 0.15);
  z-index: 3;
}

.onevr-card-feature-icon {
  width: 28px;
  height: 28px;
  filter: invert(24%) sepia(48%) saturate(1523%) hue-rotate(198deg) brightness(95%) contrast(91%);
}

.onevr-card-text-area {
  margin-top: 36px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.onevr-card-title-text {
  font-family: 'Roboto', sans-serif;
  font-size: 22px;
  font-weight: 600;
  color: #14151F;
  margin: 0;
  line-height: 1.3;
  letter-spacing: -0.5px;
}

.onevr-card-desc-text {
  font-family: 'Roboto', sans-serif;
  font-size: 15px;
  line-height: 1.6;
  color: rgba(20, 21, 31, 0.75);
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.onevr-card-footer-area {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: auto;
  padding-top: 24px;
}

.onevr-card-cta-text {
  font-family: 'Roboto', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #285096;
  transition: color 0.3s;
}

.onevr-card-arrow-icon {
  color: #285096;
  transition: transform 0.3s ease;
}

.onevr-feature-card-item:hover .onevr-card-arrow-icon {
  transform: translateX(4px);
}

.onevr-integration-wrapper {
  padding: 60px 3rem 100px;
  margin: 0 auto;
  max-width: 1500px;
  text-align: center;
}

.onevr-integration-badge {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 10px 24px;
  background: rgba(255, 255, 255, 0.95);
  border: 2px solid rgba(40, 80, 150, 0.3);
  border-radius: 50px;
  margin-bottom: 28px;
  box-shadow: 0 4px 16px rgba(40, 80, 150, 0.12);
}

.onevr-integration-dot {
  width: 10px;
  height: 10px;
  background: #285096;
  border-radius: 50%;
  animation: onevr-pulse 2.5s ease-in-out infinite;
}

@keyframes onevr-pulse {
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(40, 80, 150, 0.7); }
  50% { transform: scale(1.2); box-shadow: 0 0 0 8px rgba(40, 80, 150, 0); }
}

.onevr-integration-badge-label {
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #285096;
}

.onevr-integration-main-title {
  font-size: clamp(42px, 6vw, 64px);
  font-weight: 800;
  color: #14151F;
  margin: 0 0 28px;
  letter-spacing: -2px;
  line-height: 1.2;
}

.onevr-integration-title-accent {
  color: #285096;
}

.onevr-integration-subtitle-text {
  font-size: clamp(16px, 2vw, 20px);
  line-height: 1.8;
  color: rgba(20, 21, 31, 0.7);
  max-width: 950px;
  margin: 0 auto 60px;
}

.onevr-integration-grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 40px;
  margin-top: 60px;
}

.onevr-integration-card-item {
  position: relative;
  background: #FFFFFF;
  padding: 48px 32px;
  border-radius: 20px;
  transition: all 0.4s ease;
  border: 1px solid #D4D9DE;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  text-align: center;
  opacity: 1;
  transform: translateY(0);
}

.onevr-integration-card-item:hover {
  transform: translateY(-12px);
  border-color: #285096;
  box-shadow: 0 12px 32px rgba(40, 80, 150, 0.2);
}

.onevr-integration-icon-box {
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.4s ease;
}

.onevr-integration-icon-bg {
  position: absolute;
  inset: 0;
  border-radius: 16px;
  background: #EDF3FD;
  transition: all 0.4s;
}

.onevr-integration-card-item:hover .onevr-integration-icon-bg {
  background: #285096;
  box-shadow: 0 12px 24px rgba(40, 80, 150, 0.3);
}

.onevr-integration-card-item:hover .onevr-integration-icon-box {
  transform: scale(1.1);
}

.onevr-integration-main-icon {
  width: 40px;
  height: 40px;
  filter: invert(24%) sepia(48%) saturate(1523%) hue-rotate(198deg) brightness(95%) contrast(91%);
  transition: filter 0.4s;
  z-index: 1;
  position: relative;
}

.onevr-integration-card-item:hover .onevr-integration-main-icon {
  filter: brightness(0) invert(1);
}

.onevr-integration-card-title {
  font-size: 22px;
  font-weight: 700;
  color: #14151F;
  margin: 0 0 12px;
  line-height: 1.3;
}

.onevr-integration-card-desc {
  font-size: 15px;
  line-height: 1.6;
  color: rgba(20, 21, 31, 0.7);
  margin: 0;
}

.onevr-footer-main {
  position: relative;
  background: linear-gradient(135deg, #0a1929 0%, #1a2f42 100%);
  color: #e2e8f0;
  padding: 100px 0 0;
  margin-top: 80px;
  z-index: 10;
}

.onevr-footer-content-wrapper {
  max-width: 1500px;
  margin: 0 auto;
  padding: 0 3rem;
}

.onevr-footer-grid-layout {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr;
  gap: 80px;
  padding-bottom: 70px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.onevr-footer-col {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.onevr-footer-logo-img {
  height: 55px;
  width: auto;
  filter: drop-shadow(0 4px 12px rgba(40, 80, 150, 0.25));
}

.onevr-footer-tagline-text {
  font-size: 16px;
  line-height: 1.7;
  color: #94a3b8;
  max-width: 350px;
}

.onevr-footer-company-text {
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
}

.onevr-footer-heading-text {
  font-size: 19px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 12px;
}

.onevr-footer-details-list {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.onevr-footer-address-item {
  display: flex;
  gap: 14px;
  align-items: start;
  font-size: 14px;
  line-height: 1.6;
  color: #cbd5e1;
}

.onevr-footer-icon-svg {
  flex-shrink: 0;
  color: #285096;
  margin-top: 3px;
}

.onevr-footer-link-item {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 15px;
  color: #cbd5e1;
  text-decoration: none;
  transition: all 0.3s;
}

.onevr-footer-link-item:hover {
  color: #285096;
  transform: translateX(6px);
}

.onevr-footer-contact-btn {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 14px 28px;
  background: #285096;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 6px 20px rgba(40, 80, 150, 0.35);
  margin-top: 12px;
  width: fit-content;
}

.onevr-footer-contact-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(40, 80, 150, 0.45);
}

.onevr-footer-bottom-area {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 40px 0;
  flex-wrap: wrap;
  gap: 24px;
}

.onevr-footer-copyright-text {
  font-size: 14px;
  color: #94a3b8;
}

.onevr-footer-legal-links {
  display: flex;
  align-items: center;
  gap: 20px;
}

.onevr-footer-legal-link {
  font-size: 14px;
  color: #cbd5e1;
  text-decoration: none;
  transition: color 0.3s;
  position: relative;
}

.onevr-footer-legal-link:hover {
  color: #285096;
}

.onevr-footer-separator-dot {
  color: #475569;
}

@media (max-width: 1400px) {
  .onevr-features-grid-container {
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  }
}

@media (max-width: 1200px) {
  .onevr-features-grid-container {
    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
    gap: 28px;
  }
  .onevr-footer-grid-layout {
    grid-template-columns: 1fr 1fr;
    gap: 50px;
  }
  .onevr-footer-company-col {
    grid-column: 1 / -1;
  }
}

@media (max-width: 768px) {
  .onevr-hero-wrapper {
    padding: 24px 1.5rem 60px;
  }
  .onevr-features-wrapper {
    padding: 40px 1.5rem 60px;
  }
  .onevr-features-grid-container {
    grid-template-columns: 1fr;
  }
  .onevr-integration-wrapper {
    padding: 60px 1.5rem 80px;
  }
  .onevr-integration-grid-container {
    grid-template-columns: 1fr;
  }
  .onevr-footer-grid-layout {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  .onevr-footer-bottom-area {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>